import {useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Separator} from '@common-web/components/ui/separator';
import {Button} from '@common-web/components/ui/button';
import {useRfcContext} from '../../RfcContext';
import {FileUpload} from '../../components/FileUpload';
import {RfcAttachmentType, RfcStep} from '@common/types/rfc';
import {trpc} from '@common-web/lib/trpc';
import {RfcReadOnlyView, RfcReviewSection} from '@common-web/components/rfc/RfcReadOnlyView';
import {RfcStepForm} from '@client/pages/rfc/forms/RfcStepForm';
import {RfcSection125Form, RfcMecForm, RfcClientPortalAccessForm} from '@common/lib/rfcSchema';
import {Check, Landmark, Pencil} from 'lucide-react';

type WizardFormValues = {
  section125Form: RfcSection125Form;
  mecForm: RfcMecForm;
  clientPortalAccessForm: RfcClientPortalAccessForm;
};

/**
 * Review sections the client can edit inline, mapped to the wizard step whose
 * form is rendered in place of the read-only content. Agent-of-record sections
 * are intentionally absent — clients never fill those in the wizard.
 */
const EDITABLE_SECTION_STEPS: Partial<Record<RfcReviewSection, RfcStep>> = {
  section125GroupInfo: RfcStep.SECTION_125_FORM_GROUP_INFO,
  section125Admin: RfcStep.SECTION_125_FORM_ADMIN,
  section125Contacts: RfcStep.SECTION_125_FORM_CONTACTS,
  section125Payroll: RfcStep.SECTION_125_FORM_PAYROLL_INFO,
  mecGroupInfo: RfcStep.MEC_FORM_GROUP_INFO,
  mecAdmin: RfcStep.MEC_FORM_ADMIN,
  mecContacts: RfcStep.MEC_FORM_CONTACTS,
  mecPayroll: RfcStep.MEC_FORM_PAYROLL_INFO,
  clientPortalAccess: RfcStep.CLIENT_PORTAL_ACCESS,
};

function EditToggleButton({isEditing, onClick}: {isEditing: boolean; onClick: () => void}) {
  return (
    <Button
      type='button'
      variant='outline'
      size='sm'
      onClick={onClick}
      className='shrink-0 bg-white text-brand-navy hover:bg-gray-50 border-gray-200'
    >
      {isEditing ? (
        <>
          <Check className='h-4 w-4 mr-1' />
          Done
        </>
      ) : (
        <>
          <Pencil className='h-4 w-4 mr-1' />
          Edit
        </>
      )}
    </Button>
  );
}

export function ReviewSummary() {
  const {attachments = [], refreshAttachments, accessToken, password, saveDraft} = useRfcContext();
  const {watch} = useFormContext<WizardFormValues>();
  // Live form values so inline edits are reflected in the summary immediately,
  // without waiting for a draft save + refetch.
  const formValues = watch();

  // Only one inline editor may be open at a time: the ACH step form registers
  // the same Group Information banking fields, so two mounted RfcStepForm
  // instances would fight over the same react-hook-form registrations.
  const [openEditor, setOpenEditor] = useState<RfcReviewSection | 'ach' | null>(null);

  const rfcQuery = trpc.clients.rfc.getByToken.useQuery(
    {accessToken, password},
    {enabled: !!accessToken && !!password},
  );

  // Treat loading/failed/undefined as not enabled so the bank-letter/voided-check
  // upload sections only render once the tenant flag is confirmed true.
  const achEnabled = rfcQuery.data?.achAuthorizationEnabled === true;

  // Closing an editor — via its Done button or implicitly by opening another
  // editor — saves the draft.
  const toggleEditor = (editor: RfcReviewSection | 'ach') => {
    if (openEditor !== null) void saveDraft?.();
    setOpenEditor(openEditor === editor ? null : editor);
  };

  const sectionHeaderAction = (section: RfcReviewSection) => {
    if (!EDITABLE_SECTION_STEPS[section]) return undefined;
    return (
      <EditToggleButton isEditing={openEditor === section} onClick={() => toggleEditor(section)} />
    );
  };

  const sectionContentOverride = (section: RfcReviewSection) => {
    const step = EDITABLE_SECTION_STEPS[section];
    if (!step || openEditor !== section) return undefined;
    return <RfcStepForm currentStep={step} />;
  };

  const attachmentsByType = (type: RfcAttachmentType) =>
    attachments
      .filter((a) => a.fileType === type)
      .map((a) => ({
        id: a.id,
        fileName: a.fileName,
        fileSize: a.fileSize,
      }));

  return (
    <div className='space-y-8'>
      {!!rfcQuery.data && !rfcQuery.data.requiresPassword && (
        <>
          <RfcReadOnlyView
            data={{
              section125FormEnabled: !!rfcQuery.data.section125FormEnabled,
              mecFormEnabled: !!rfcQuery.data.mecFormEnabled,
              section125Form: formValues.section125Form,
              mecForm: formValues.mecForm,
              clientPortalAccessForm: formValues.clientPortalAccessForm,
            }}
            showClientPortalAccess={rfcQuery.data.clientPortalAccessEnabled}
            sectionHeaderAction={sectionHeaderAction}
            sectionContentOverride={sectionContentOverride}
          />
          {achEnabled && (
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <div className='flex items-center justify-between gap-3'>
                  <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                    <Landmark className='h-5 w-5 text-brand-blue' />
                    ACH Authorization
                  </CardTitle>
                  <EditToggleButton
                    isEditing={openEditor === 'ach'}
                    onClick={() => toggleEditor('ach')}
                  />
                </div>
              </CardHeader>
              <CardContent className='space-y-4 pt-5'>
                {openEditor === 'ach' ? (
                  <RfcStepForm currentStep={RfcStep.ACH_FORM} />
                ) : (
                  <p className='text-sm text-brand-gray'>
                    Banking details for each location are shown in the Group Information section
                    above. Click Edit to update them.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Attach Supporting Documents</CardTitle>
        </CardHeader>
        <CardContent className='space-y-8'>
          {achEnabled && (
            <>
              <div className='space-y-3'>
                <h4 className='font-medium text-lg'>
                  Bank Letter or Voided Check (At least one required)
                </h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FileUpload
                    fileType={RfcAttachmentType.BANK_LETTER}
                    multiple={true}
                    accept='.pdf,.png,.jpg,.jpeg'
                    existingFiles={attachmentsByType(RfcAttachmentType.BANK_LETTER)}
                    onChange={refreshAttachments}
                  />
                  <FileUpload
                    fileType={RfcAttachmentType.VOIDED_CHECK}
                    multiple={true}
                    accept='.pdf,.png,.jpg,.jpeg'
                    existingFiles={attachmentsByType(RfcAttachmentType.VOIDED_CHECK)}
                    onChange={refreshAttachments}
                  />
                </div>
              </div>
              <Separator />
            </>
          )}
          <div className='space-y-3'>
            <h4 className='font-medium text-lg'>Other Supporting Documents (Optional)</h4>
            <FileUpload
              fileType={RfcAttachmentType.OTHER}
              multiple={true}
              accept='.pdf,.csv,.xlsx,.xls'
              existingFiles={attachmentsByType(RfcAttachmentType.OTHER)}
              onChange={refreshAttachments}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
